import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import ProductCard from '@components/ProductCardNew/ProductCard';
import { IProductObject, TAlgoliaProductHit } from '@features/categories/typescript';
import styles from '@components/ProductList/ProductList.styles';
import { IInfoCardWithMedia, IParsedImage } from '@components/typescript';
import InfoCardWithMedia from '@components/InfoCardWithMedia/InfoCardWithMedia';
import { PRODUCTS_RENDER_COUNTS, SPECIAL_ITEM_POSITIONS } from '@features/categories/const';
import ArrangementProductState from '@features/categories/mobx/state/ArrangementProductState';
import ShopTheLook from '@components/ShopTheLook/ShopTheLook';
import ReaffiliatesProductListing from '@reaffiliates/ReaffiliatesProductListing';
import { useOffer } from '@hooks/useOffer';
import ReaffiliatesSelectItem from '@reaffiliates/ReaffiliatesSelectItem';
import LayoutUiState from '@features/layout/mobX/LayoutUiState';
import { TProductGallerySlide } from '@features/cms/typescript';
import ProductGalleryBlockModal from '@features/cms/components/collections/ProductGalleryBlock/ProductGalleryBlockModal/ProductGalleryBlockModal';
import useBodyOverflow from '@hooks/useBodyOverflow';
import { IHitsInsights } from '@components/Search/typescript';
const DynamicModalRootPortal = dynamic(() => import('@components/ModalRootPortal/ModalRootPortal'), {
  ssr: false
});
type PropsType = {
  products: IProductObject[];
  infoCards?: IInfoCardWithMedia[];
  arrangementProducts?: ArrangementProductState[];
  getArrangementProductProducts?: (arrangementProductId: number) => Promise<IProductObject[]>;
  category?: string;
  productsCount?: number;
  listingType?: string;
  listingId?: number;
  hitsInsights?: IHitsInsights<TAlgoliaProductHit>;
  onProductClick?: () => void;
};
const ProductList = ({
  products,
  infoCards = undefined,
  arrangementProducts = undefined,
  getArrangementProductProducts = undefined,
  category = '',
  productsCount = 0,
  listingType = 'product_listing',
  listingId = 0,
  hitsInsights = {
    hits: [],
    sendEvent: () => null
  },
  onProductClick = undefined
}: PropsType) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<TProductGallerySlide<IParsedImage> | null>(null);
  const lastVisitedProductRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const {
    setLastVisitedProductId,
    lastVisitedProductId
  } = LayoutUiState;
  const {
    query,
    asPath
  } = useRouter();
  const {
    slug
  } = query;
  const [_, priceListName] = useOffer();
  const newSlug = slug && slug?.length && slug[1] ? slug[1] : '';
  useBodyOverflow(isModalOpen, 'productList', false, true);
  useEffect(() => {
    if (lastVisitedProductId && lastVisitedProductRef.current && !timeoutRef.current) {
      timeoutRef.current = setTimeout(() => {
        lastVisitedProductRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
        setLastVisitedProductId(null);
      }, 100);
    }
    return () => {
      timeoutRef.current = null;
    };
  }, []);
  useEffect(() => {
    if (products.length) {
      ReaffiliatesProductListing(products, category || newSlug, priceListName, productsCount, listingType, listingId);
    }
  }, [products.length]);
  const selectFunction = (product: IProductObject) => {
    if (onProductClick) {
      onProductClick();
    }
    ReaffiliatesSelectItem(product, category || newSlug, priceListName, listingType, listingId);
  };
  const getModalData = async (arrangementProduct: ArrangementProductState) => {
    if (getArrangementProductProducts) {
      setModalOpen(true);
      const productsData: IProductObject[] = await getArrangementProductProducts(arrangementProduct.id);
      setModalData({
        products: productsData,
        image: arrangementProduct.imageWithBlur,
        video: arrangementProduct.video,
        itemId: 'singleImage'
      });
    }
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setModalData(null);
  };
  const handleProductClick = (productClicked: IProductObject) => {
    selectFunction(productClicked);
    if (hitsInsights.hits.length) {
      const hit = hitsInsights.hits.find(hitObject => hitObject.id_product === productClicked.id);
      if (hit) {
        hitsInsights.sendEvent('click', hit, 'Hit Clicked');
      }
    }
  };
  const renderProductList = useMemo(() => {
    let itemListIndex = -1;
    let infoCardIndex = -1;
    let arrangementIndex = -1;
    return products.map(product => {
      itemListIndex = itemListIndex >= PRODUCTS_RENDER_COUNTS ? 1 : itemListIndex + 1;
      const shouldShowSpecialCard = SPECIAL_ITEM_POSITIONS.includes(itemListIndex);
      const addRef = lastVisitedProductId === product.id;
      const renderSpecialCard = () => {
        if (itemListIndex === SPECIAL_ITEM_POSITIONS[0] && arrangementProducts && arrangementProducts.length) {
          arrangementIndex = arrangementIndex === arrangementProducts.length - 1 ? 0 : arrangementIndex + 1;
          return arrangementProducts[arrangementIndex].image.desktop || arrangementProducts[arrangementIndex].video.desktop.data ? <div sx={styles.specialCol}>
              {/* @ts-expect-error - React 19 compatibility issue with memo types */}
              <ShopTheLook arrangementProduct={arrangementProducts[arrangementIndex]} getModalData={getModalData} />
            </div> : null;
        }
        if (infoCards && infoCards.length) {
          infoCardIndex = infoCardIndex === infoCards.length - 1 ? 0 : infoCardIndex + 1;
          return <div sx={styles.specialCol}>
              {/* @ts-expect-error - React 19 compatibility issue with memo types */}
              <InfoCardWithMedia infoCard={infoCards[infoCardIndex]} />
            </div>;
        }
        return <></>;
      };
      return (
        // @ts-expect-error - React 19 compatibility issue with Fragment types
        <Fragment key={`${product.id}${itemListIndex}${Math.random()}`}>
          {shouldShowSpecialCard && renderSpecialCard()}
          <div sx={styles.productCol} ref={addRef ? lastVisitedProductRef : null}>
            <ProductCard product={product} selectFunction={() => handleProductClick(product)} />
          </div>
        </Fragment>
      );
    });
  }, [products, arrangementProducts, infoCards, asPath]);
  return <>
      {renderProductList}
      {isModalOpen && modalData &&
    // @ts-expect-error - React 19 compatibility issue with next/dynamic types
    <DynamicModalRootPortal>
          <ProductGalleryBlockModal isActive={isModalOpen} slide={modalData} onClose={handleCloseModal} />
        </DynamicModalRootPortal>}
    </>;
};
export default ProductList;